import { Component, EventEmitter, Output, computed, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NetworkEntry } from './network.types';
import { NetworkMonitorService } from './network-monitor.service';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';

type ExpandedSection = null | 'body' | 'response' | 'headers';

@Component({
  selector: 'app-network-row',
  standalone: true,
  imports: [MatIconModule, JsonViewerComponent],
  template: `
    <div class="row" [class.pending]="entry().state === 'pending'" [class.cancelled]="entry().state === 'cancelled'" [class.flash]="recentlyResolved()" [class.pinned]="entry().pinned">
      <span class="cell status-cell">
        <span class="status-badge" [class]="'status-' + statusClass()">
          @if (entry().state === 'pending') { … } @else if (entry().state === 'cancelled') { CXL } @else { {{ entry().status }} }
        </span>
        @if (entry().mocked) {
          <span class="mocked-label">MOCK</span>
        }
      </span>

      <span class="cell time" [class]="'time-' + timeClass()">
        @if (pendingWarning()) {
          <mat-icon class="pending-warn-icon" title="Request pending for a long time">warning</mat-icon>
        }
        {{ timeText() }}
      </span>

      <span class="cell size">{{ sizeText() }}</span>

      <span class="cell method" [class]="'method-' + entry().method.toLowerCase()">{{ entry().method }}</span>

      <span class="cell path" [title]="entry().path">{{ entry().path }}</span>

      <span class="cell data-cell">
        @if (hasBody()) {
          <span class="data-preview" (click)="toggleSection('body')" [title]="bodyPreview()">{{ bodyPreview() }}</span>
          <button class="icon-btn" (click)="toggleSection('body')" [title]="expandedSection() === 'body' ? 'Collapse body' : 'Expand body'">
            <mat-icon>{{ expandedSection() === 'body' ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
          <button class="icon-btn" (click)="copyBody()" title="Copy request body">
            <mat-icon>{{ copiedField() === 'body' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        } @else {
          <span class="data-preview empty">—</span>
        }
      </span>

      <span class="cell expand">
        <button class="icon-btn" (click)="toggleSection('response')" [disabled]="!hasResponse()" title="Toggle response">
          <mat-icon>{{ expandedSection() === 'response' ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
      </span>

      <span class="cell actions">
        <button class="icon-btn" (click)="toggleSection('headers')" title="Request headers"
                [class.active-section]="expandedSection() === 'headers'">
          <mat-icon>description</mat-icon>
        </button>
        <button class="icon-btn" (click)="relaunch.emit(entry())" title="Relaunch with current token">
          <mat-icon>replay</mat-icon>
        </button>
        <button class="icon-btn" (click)="saveFavorite.emit(entry())" title="Save to favorites">
          <mat-icon>star_border</mat-icon>
        </button>
        <button class="icon-btn" (click)="createMock.emit(entry())" title="Create mock from this request">
          <mat-icon>science</mat-icon>
        </button>
        <button class="icon-btn" (click)="onCopyToken()" title="Copy auth token">
          <mat-icon>{{ copiedField() === 'token' ? 'check' : 'vpn_key' }}</mat-icon>
        </button>
        <button class="icon-btn" [class.pinned]="entry().pinned" (click)="pin.emit(entry().id)"
                [title]="entry().pinned ? 'Unpin' : 'Pin (survives clear)'">
          <mat-icon>{{ entry().pinned ? 'push_pin' : 'push_pin' }}</mat-icon>
        </button>
        <button class="icon-btn danger" [class.confirming]="confirmingDelete()"
                (click)="onDeleteClick()" [title]="confirmingDelete() ? 'Click again to confirm' : 'Remove'">
          @if (confirmingDelete()) {
            <span class="confirm-text">Sure?</span>
          } @else {
            <mat-icon>close</mat-icon>
          }
        </button>
      </span>
    </div>

    @if (expandedSection() === 'body' && hasBody()) {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Request body</span>
          <button class="icon-btn" (click)="copyBody()" title="Copy">
            <mat-icon>{{ copiedField() === 'body' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
        <app-json-viewer [data]="entry().requestBody" mode="edit" [resizable]="true" [initialHeight]="160"
                         (save)="onBodySave($event)" />
      </div>
    }

    @if (expandedSection() === 'response' && hasResponse()) {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Response</span>
          <button class="icon-btn" (click)="copyResponse()" title="Copy">
            <mat-icon>{{ copiedField() === 'response' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
        <app-json-viewer [data]="entry().response" mode="view" [resizable]="true" [initialHeight]="160" />
      </div>
    }

    @if (expandedSection() === 'headers') {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Request headers</span>
          <button class="icon-btn" (click)="copyHeaders()" title="Copy headers">
            <mat-icon>{{ copiedField() === 'headers' ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>
        <div class="headers-list">
          @for (h of headerEntries(); track h[0]) {
            <div class="header-row">
              <span class="header-key">{{ h[0] }}:</span>
              <span class="header-val">{{ h[1] }}</span>
            </div>
          }
          @if (headerEntries().length === 0) {
            <div class="headers-empty">No headers captured</div>
          }
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      border-bottom: 1px solid #23233a;
    }

    .row {
      display: grid;
      grid-template-columns: 48px 56px 38px 52px minmax(0, 1.5fr) minmax(0, 1fr) 30px 130px;
      align-items: center;
      gap: 4px;
      padding: 2px 6px;
      font-size: 0.72rem;
      color: #d4d4d8;
      min-height: 26px;
    }
    .row:hover { background: #23233a; }
    .row.pinned { border-left: 2px solid #fbbf24; }
    .row.flash { animation: rowSlideIn 0.35s ease-out; }
    @keyframes rowSlideIn {
      0% { opacity: 0; transform: translateX(-20px); }
      100% { opacity: 1; transform: translateX(0); }
    }

    .cell {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    /* Status cell (badge + mocked label) */
    .status-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1px;
      min-width: 0;
    }
    .status-badge {
      padding: 1px 4px;
      border-radius: 3px;
      text-align: center;
      font-weight: 700;
      font-size: 0.66rem;
      font-family: ui-monospace, monospace;
      color: #fff;
      width: 100%;
    }
    .status-pending     { background: #6b7280; }
    .status-success     { background: #16a34a; }
    .status-redirect    { background: #3b82f6; }
    .status-client-error{ background: #f97316; }
    .status-server-error{ background: #dc2626; }
    .status-error       { background: #991b1b; }
    .status-cancelled   { background: #78716c; }

    .mocked-label {
      font-size: 0.52rem;
      font-weight: 700;
      font-family: ui-monospace, monospace;
      color: #fbbf24;
      letter-spacing: 0.05em;
      line-height: 1;
    }

    .row.pending .status-badge { animation: pulse 1s infinite; }
    .row.cancelled { opacity: 0.6; }
    .row.cancelled .path { text-decoration: line-through; }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.55; }
    }

    /* Time */
    .time {
      font-family: ui-monospace, monospace;
      font-size: 0.68rem;
      color: #94a3b8;
      text-align: right;
      padding-right: 4px;
    }
    .time-pending { color: #fbbf24; }
    .time-warn    { color: #f97316; }
    .time-slow    { color: #dc2626; font-weight: 700; }
    .pending-warn-icon {
      font-size: 11px;
      width: 11px;
      height: 11px;
      color: #f97316;
      vertical-align: middle;
      margin-right: 1px;
    }

    /* Size */
    .size {
      font-family: ui-monospace, monospace;
      font-size: 0.6rem;
      color: #64748b;
      text-align: right;
    }

    /* Method */
    .method {
      font-weight: 700;
      font-size: 0.66rem;
      font-family: ui-monospace, monospace;
      text-align: center;
    }
    .method-get    { color: #3b82f6; }
    .method-post   { color: #16a34a; }
    .method-put    { color: #f97316; }
    .method-delete { color: #dc2626; }
    .method-patch  { color: #a855f7; }

    /* Path */
    .path {
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      color: #e5e7eb;
    }

    /* Data cell */
    .data-cell {
      display: flex;
      gap: 1px;
      align-items: center;
      min-width: 0;
    }
    .data-preview {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-family: ui-monospace, monospace;
      font-size: 0.68rem;
      color: #cbd5e1;
      cursor: pointer;
      padding-right: 2px;
    }
    .data-preview:hover { color: #fff; text-decoration: underline; }
    .data-preview.empty {
      color: #4b5563;
      cursor: default;
      text-align: center;
    }
    .data-preview.empty:hover { text-decoration: none; }

    /* Expand toggle */
    .expand {
      display: flex;
      justify-content: center;
    }

    /* Actions */
    .actions {
      display: flex;
      gap: 1px;
      align-items: center;
      justify-content: flex-end;
    }

    /* Icon buttons */
    .icon-btn {
      background: none;
      border: none;
      color: #9ca3af;
      padding: 2px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      flex-shrink: 0;
    }
    .icon-btn mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
    }
    .icon-btn:hover:not(:disabled) {
      background: #374151;
      color: #fff;
    }
    .icon-btn:disabled {
      opacity: 0.25;
      cursor: not-allowed;
    }
    .icon-btn.danger:hover:not(:disabled) {
      background: #991b1b;
      color: #fff;
    }
    .icon-btn.pinned { color: #fbbf24; }
    .icon-btn.active-section { color: #3b82f6; }
    .icon-btn.confirming {
      background: #991b1b;
      color: #fff;
      animation: confirmPulse 0.6s ease-in-out infinite alternate;
    }
    @keyframes confirmPulse {
      from { opacity: 1; }
      to { opacity: 0.6; }
    }
    .confirm-text {
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    /* Expanded sections (body / response) */
    .section-expanded {
      background: #0a0a18;
      padding: 4px 6px 8px 6px;
    }
    .section-header {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 4px;
    }
    .section-label {
      font-size: 0.66rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex: 1;
    }
    .section-expanded app-json-viewer {
      display: block;
    }

    /* Headers list */
    .headers-list {
      padding: 4px 8px;
      font-family: ui-monospace, monospace;
      font-size: 0.68rem;
    }
    .header-row {
      display: flex;
      gap: 6px;
      padding: 1px 0;
      line-height: 1.4;
    }
    .header-key {
      color: #94a3b8;
      font-weight: 600;
      flex-shrink: 0;
    }
    .header-val {
      color: #e5e7eb;
      word-break: break-all;
    }
    .headers-empty {
      color: #4b5563;
      font-style: italic;
      padding: 4px 0;
    }
  `],
})
export class NetworkRowComponent {
  readonly entry = input.required<NetworkEntry>();
  @Output() relaunch = new EventEmitter<NetworkEntry>();
  @Output() saveFavorite = new EventEmitter<NetworkEntry>();
  @Output() createMock = new EventEmitter<NetworkEntry>();
  @Output() copyToken = new EventEmitter<void>();
  @Output() pin = new EventEmitter<string>();
  @Output() delete = new EventEmitter<string>();

  private readonly monitor = inject(NetworkMonitorService);

  readonly expandedSection = signal<ExpandedSection>(null);

  /** ms elapsed (ticking when pending, frozen on completion). */
  readonly timeText = computed(() => {
    const e = this.entry();
    const reference = e.state === 'pending' ? this.monitor.tick() : (e.finishedAt ?? e.startedAt);
    return this.formatDuration(reference - e.startedAt);
  });

  readonly pendingWarning = computed(() => {
    const e = this.entry();
    if (e.state !== 'pending') return false;
    return (this.monitor.tick() - e.startedAt) > 5000;
  });

  readonly timeClass = computed(() => {
    const e = this.entry();
    if (e.state === 'pending') return 'pending';
    const ms = e.responseTimeMs ?? (e.finishedAt ? e.finishedAt - e.startedAt : 0);
    if (ms >= 3000) return 'slow';
    if (ms >= 1000) return 'warn';
    return 'normal';
  });

  readonly statusClass = computed(() => {
    const e = this.entry();
    if (e.state === 'pending') return 'pending';
    if (e.state === 'cancelled') return 'cancelled';
    const s = e.status ?? 0;
    if (s >= 200 && s < 300) return 'success';
    if (s >= 300 && s < 400) return 'redirect';
    if (s >= 400 && s < 500) return 'client-error';
    if (s >= 500) return 'server-error';
    return 'error';
  });

  readonly hasBody = computed(() => {
    const b = this.entry().requestBody;
    return b !== null && b !== undefined;
  });

  readonly hasResponse = computed(() => {
    const e = this.entry();
    return e.finishedAt !== undefined && e.response !== null && e.response !== undefined;
  });

  readonly sizeText = computed(() => {
    const e = this.entry();
    const size = e.responseSize ?? e.requestSize ?? 0;
    if (!size) return '';
    if (size < 1024) return size + 'B';
    return (size / 1024).toFixed(1) + 'K';
  });

  readonly bodyPreview = computed(() => this.preview(this.entry().requestBody));

  readonly recentlyResolved = computed(() => {
    const e = this.entry();
    if (e.state === 'pending' || !e.finishedAt) return false;
    return (Date.now() - e.finishedAt) < 1500;
  });

  readonly headerEntries = computed(() => Object.entries(this.entry().requestHeaders || {}));

  /** Inline delete confirmation (#61) */
  readonly confirmingDelete = signal(false);
  private deleteTimer: ReturnType<typeof setTimeout> | null = null;

  /** Copy visual feedback (#63) — tracks which field just copied */
  readonly copiedField = signal<'body' | 'response' | 'headers' | 'token' | null>(null);
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  toggleSection(section: 'body' | 'response' | 'headers'): void {
    this.expandedSection.update(cur => cur === section ? null : section);
  }

  onBodySave(payload: unknown): void {
    this.monitor.updateEntryBody(this.entry().id, payload);
  }

  copyBody(): void {
    navigator.clipboard.writeText(this.stringify(this.entry().requestBody));
    this.showCopyFeedback('body');
  }

  copyResponse(): void {
    navigator.clipboard.writeText(this.stringify(this.entry().response));
    this.showCopyFeedback('response');
  }

  copyHeaders(): void {
    const text = this.headerEntries().map(([k, v]) => `${k}: ${v}`).join('\n');
    navigator.clipboard.writeText(text);
    this.showCopyFeedback('headers');
  }

  /** Inline delete: first click shows "Sure?", second click within 2s deletes. */
  onDeleteClick(): void {
    if (this.confirmingDelete()) {
      if (this.deleteTimer) clearTimeout(this.deleteTimer);
      this.confirmingDelete.set(false);
      this.delete.emit(this.entry().id);
    } else {
      this.confirmingDelete.set(true);
      this.deleteTimer = setTimeout(() => this.confirmingDelete.set(false), 2000);
    }
  }

  onCopyToken(): void {
    this.copyToken.emit();
    this.showCopyFeedback('token');
  }

  private showCopyFeedback(field: 'body' | 'response' | 'headers' | 'token'): void {
    if (this.copyTimer) clearTimeout(this.copyTimer);
    this.copiedField.set(field);
    this.copyTimer = setTimeout(() => this.copiedField.set(null), 1500);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private formatDuration(ms: number): string {
    if (ms < 0) return '';
    if (ms < 1000) return Math.round(ms) + 'ms';
    return (ms / 1000).toFixed(2) + 's';
  }

  private preview(v: unknown): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'string') return v.length > 60 ? v.slice(0, 60) + '…' : v;
    try {
      const s = JSON.stringify(v);
      return s.length > 60 ? s.slice(0, 60) + '…' : s;
    } catch {
      return String(v);
    }
  }

  private stringify(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
  }
}
